import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronLeft, ChevronRight, ImagePlus, Sparkles, Star, Trash2, X } from 'lucide-react';
import {
  DECOR_LIBRARY_CATEGORIES,
  DECOR_LIBRARY_CATEGORY_SPECS,
  DECOR_LIBRARY_DOCUMENT_TYPE,
  DECOR_LIBRARY_ENTITY_TYPE,
  DEFAULT_DECOR,
  MAX_DECOR_LIBRARY_PICTURES,
  MAX_DECOR_LIBRARY_READ_PICTURES,
  MAX_DECOR_LIBRARY_TAGS,
  MAX_DECOR_LIBRARY_VENDORS,
  categoryLook,
  normalizeDecorTags,
  type DecorLibraryCategory,
  type DecorLibraryItem,
  type DecorLook,
} from '@peles/shared';
import { EntityModal } from '../common';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { SearchableSelect } from '../common/SearchableSelect';
import { useDialog } from '../../context/DialogContext';
import {
  createEntityAttachment,
  deleteEntityAttachment,
  getAttachmentUploadUrl,
} from '../../api/entity-attachments';
import { uploadFileToS3 } from '../../api/photos';
import {
  useCreateDecorLibraryItem,
  useDeleteDecorLibraryItem,
  useReadDecorLibraryPictures,
  useUpdateDecorLibraryItem,
} from '../../hooks/useDecorLibrary';
import { combineColors, loadPixels, weightedColors } from '../../utils/imagePalette';
import { pictureProblem } from '../floor-plan/look/useDecorPictures';
import { DecorFieldsEditor } from './DecorFieldsEditor';

/** One picture on the form: saved (`id`), or uploaded and waiting for Save (`s3Key`). */
export interface DraftPicture {
  key: string;
  id?: string;
  s3Key?: string;
  url: string | null;
  fileName: string | null;
  mimeType: string | null;
  size?: number;
  file?: File;
}

interface DraftVendor {
  vendorId: string;
  name: string;
  notes: string;
}

export interface DecorLibraryItemModalProps {
  open: boolean;
  onClose: () => void;
  /** The piece to edit; none makes a new one. */
  item?: DecorLibraryItem | null;
  /** A new piece's category. */
  defaultCategory?: DecorLibraryCategory | null;
  /** Told the piece as saved — the picker picks a piece it just added. */
  onSaved?: (item: DecorLibraryItem) => void;
}

const INPUT =
  'w-full min-h-11 sm:min-h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

function errorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error
    ?.code;
}

function errorText(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return message ?? fallback;
}

let keySeq = 0;
const nextKey = () => `pic-${++keySeq}`;

/** The colours of a piece's pictures, most seen first; none when none could be read. */
export async function picturesColors(pictures: DraftPicture[]): Promise<string[]> {
  const pixels = (
    await Promise.all(
      pictures.slice(0, 4).map((p) => loadPixels(p.file ?? p.url ?? '').catch(() => null)),
    )
  ).filter((p): p is NonNullable<typeof p> => !!p);
  if (pixels.length === 0) return [];
  return combineColors(pixels.map((p) => weightedColors(p, { centre: true })));
}

/**
 * Adds or edits one piece of the decor library: its category, name and tags,
 * pictures from as many angles as the office has (the first is the cover),
 * the vendors who supply it and their notes, and how the 3D view draws it —
 * which the AI can fill in from the pictures.
 *
 * Pictures upload as they are added, so the AI can read them at once; they
 * are filed on the piece, and any taken off are deleted, only on Save.
 */
export function DecorLibraryItemModal({
  open,
  onClose,
  item,
  defaultCategory,
  onSaved,
}: DecorLibraryItemModalProps) {
  const { confirm } = useDialog();
  const create = useCreateDecorLibraryItem();
  const update = useUpdateDecorLibraryItem();
  const remove = useDeleteDecorLibraryItem();
  const read = useReadDecorLibraryPictures();

  const [category, setCategory] = useState<DecorLibraryCategory | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [look, setLook] = useState<DecorLook>({});
  const [isActive, setIsActive] = useState(true);
  const [pictures, setPictures] = useState<DraftPicture[]>([]);
  const [vendors, setVendors] = useState<DraftVendor[]>([]);
  const [uploading, setUploading] = useState(0);
  // One AI read at a time, for the whole of it — a stale upload sent again
  // included — so a second press cannot overwrite the first's answer.
  const [reading, setReading] = useState(false);
  const readingRef = useRef(false);
  // Read by callbacks a dropdown may still hold from before the form locked.
  const lockedRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // Each opening is a session: an upload or AI read still running when the
  // form closes, or moves on to another piece, finishes into nothing.
  const session = useRef(0);
  // A save's progress, so a retry after a failure resumes rather than
  // creating the piece twice or attaching a picture again.
  const savedId = useRef<string | null>(null);
  const deleted = useRef(new Set<string>());
  /** Pictures this session attached — a retry deletes any taken off since. */
  const attachedHere = useRef(new Set<string>());

  // The form starts from the piece once each time it opens.
  useEffect(() => {
    session.current += 1;
    savedId.current = null;
    deleted.current = new Set();
    attachedHere.current = new Set();
    setUploading(0);
    // A read still running for the form as it was must not hold this one busy.
    readingRef.current = false;
    setReading(false);
    if (!open) return;
    setCategory(item?.category ?? defaultCategory ?? null);
    setName(item?.name ?? '');
    setDescription(item?.description ?? '');
    setTags(item?.tags ?? []);
    setTagDraft('');
    setLook(item?.look ?? {});
    setIsActive(item?.isActive ?? true);
    setPictures(
      (item?.pictures ?? []).map((p) => ({
        key: p.id,
        id: p.id,
        url: p.url,
        fileName: p.fileName,
        mimeType: p.mimeType,
      })),
    );
    setVendors((item?.vendors ?? []).map((v) => ({ ...v, notes: v.notes ?? '' })));
    setDirty(false);
    setError(null);
    setNote(null);
  }, [open, item?.id]);

  const touch = () => setDirty(true);

  const addFiles = async (files: FileList | File[]) => {
    const mine = session.current;
    setError(null);
    const list = Array.from(files);
    const room = MAX_DECOR_LIBRARY_PICTURES - pictures.length;
    if (list.length > room) {
      setError(
        `A piece holds ${MAX_DECOR_LIBRARY_PICTURES} pictures — only the first ${Math.max(room, 0)} were added.`,
      );
    }
    for (const file of list.slice(0, Math.max(room, 0))) {
      const problem = pictureProblem(file);
      if (problem) {
        setError(problem);
        continue;
      }
      setUploading((n) => n + 1);
      try {
        const { uploadUrl, s3Key } = await getAttachmentUploadUrl(file.name, file.type);
        await uploadFileToS3(uploadUrl, file);
        if (session.current !== mine) return;
        const url = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null;
        setPictures((current) => [
          ...current,
          {
            key: nextKey(),
            s3Key,
            url,
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            file,
          },
        ]);
        touch();
      } catch {
        if (session.current !== mine) return;
        setError(`${file.name} could not be uploaded. Try again.`);
      } finally {
        if (session.current === mine) setUploading((n) => n - 1);
      }
    }
  };

  const move = (index: number, by: number) => {
    setPictures((current) => {
      const next = [...current];
      const [p] = next.splice(index, 1);
      next.splice(Math.max(0, Math.min(next.length, index + by)), 0, p);
      return next;
    });
    touch();
  };

  const addTag = () => {
    const next = normalizeDecorTags([...tags, ...tagDraft.split(',')], MAX_DECOR_LIBRARY_TAGS);
    setTags(next);
    setTagDraft('');
    touch();
  };
  const onTagKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !tagDraft && tags.length) {
      setTags(tags.slice(0, -1));
      touch();
    }
  };

  /** Uploads the pictures' files again, answering each picture's new key. */
  const reuploadPictures = async (list: DraftPicture[]): Promise<Map<string, string>> => {
    const fresh = new Map<string, string>();
    for (const p of list) {
      const { uploadUrl, s3Key } = await getAttachmentUploadUrl(p.file!.name, p.file!.type);
      await uploadFileToS3(uploadUrl, p.file!);
      fresh.set(p.key, s3Key);
    }
    return fresh;
  };

  const fillWithAi = async () => {
    if (readingRef.current) return;
    const mine = session.current;
    readingRef.current = true;
    setReading(true);
    try {
      await readPictures();
    } finally {
      // Only this form's own read clears its busy state — one reopened since
      // was cleared when it opened, and may have a read of its own running.
      if (session.current === mine) {
        readingRef.current = false;
        setReading(false);
      }
    }
  };

  const readPictures = async () => {
    const mine = session.current;
    setError(null);
    setNote(null);
    // The pictures as the form shows them, in its order — only as many as one
    // read takes. A saved picture goes by id, a new upload by key.
    const pending = pictures
      .filter((p) => p.id || p.s3Key)
      .slice(0, MAX_DECOR_LIBRARY_READ_PICTURES);
    // A new piece a failed save already created owns the pictures it attached.
    const pieceId = item?.id ?? savedId.current;
    const readSelection = (fresh?: Map<string, string>) =>
      read.mutateAsync({
        ...(pieceId ? { itemId: pieceId } : {}),
        pictures: pending.map((p) =>
          p.id ? { id: p.id } : { s3Key: fresh?.get(p.key) ?? p.s3Key! },
        ),
        category: category ?? undefined,
      });
    try {
      let suggestion;
      try {
        suggestion = await readSelection();
      } catch (err) {
        // The server reads a new picture only for a while after it was
        // uploaded. A form left open past that still holds the files, so they
        // go up again and the read is asked once more.
        const uploads = pending.filter((p) => !p.id);
        if (errorCode(err) !== 'UPLOAD_STALE' || !uploads.every((p) => p.file)) throw err;
        // A form closed or reopened while the first read was out is not retried
        // — nor its busy count touched.
        if (session.current !== mine) return;
        setUploading((n) => n + 1);
        let fresh: Map<string, string>;
        try {
          fresh = await reuploadPictures(uploads);
        } finally {
          if (session.current === mine) setUploading((n) => n - 1);
        }
        if (session.current !== mine) return;
        setPictures((current) =>
          current.map((p) => (fresh.has(p.key) ? { ...p, s3Key: fresh.get(p.key) } : p)),
        );
        suggestion = await readSelection(fresh);
      }
      if (session.current !== mine) return;
      const found = suggestion.category ?? category;
      if (!found) {
        setNote('The AI could not tell what this is. Choose the category, then try again.');
        return;
      }
      setCategory(found);
      if (!name.trim() && suggestion.name) setName(suggestion.name);
      if (!description.trim() && suggestion.description) setDescription(suggestion.description);
      setTags((current) =>
        normalizeDecorTags([...current, ...suggestion.tags], MAX_DECOR_LIBRARY_TAGS),
      );
      setLook((current) => ({ ...categoryLook(found, current), ...suggestion.look }));
      touch();
      const filled = Object.keys(suggestion.look).length;
      setNote(
        filled
          ? `Filled in ${filled} detail${filled === 1 ? '' : 's'} from the pictures. Check them, then Save.`
          : 'The AI named the piece but could not tell how to draw it — choose below.',
      );
    } catch (err) {
      if (session.current !== mine) return;
      setError(
        errorText(err, 'The AI could not read the pictures just now. Try again in a moment.'),
      );
    }
  };

  const save = async () => {
    if (!category) {
      setError('Choose what kind of piece this is.');
      return;
    }
    if (!name.trim()) {
      setError('Give the piece a name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const colors = await picturesColors(pictures).catch(() => [] as string[]);
      const fields = {
        description: description.trim() || null,
        tags,
        look: categoryLook(category, look),
        isActive,
        vendors: vendors.map((v) => ({ vendorId: v.vendorId, notes: v.notes.trim() || null })),
        ...(colors.length || pictures.length === 0 ? { colors } : {}),
      };
      const existingId = item?.id ?? savedId.current;
      let saved = existingId
        ? await update.mutateAsync({
            id: existingId,
            input: { category, name: name.trim(), ...fields },
          })
        : await create.mutateAsync({ category, name: name.trim(), ...fields });
      savedId.current = saved.id;

      // Pictures: file the new ones on the piece, delete the ones taken off,
      // then store the order.
      const ids: string[] = [];
      for (const p of pictures) {
        if (p.id) {
          ids.push(p.id);
          continue;
        }
        const attached = await createEntityAttachment({
          entityType: DECOR_LIBRARY_ENTITY_TYPE,
          entityId: saved.id,
          s3Key: p.s3Key!,
          fileName: p.fileName ?? 'picture',
          mimeType: p.mimeType ?? undefined,
          fileSizeBytes: p.size,
          documentType: DECOR_LIBRARY_DOCUMENT_TYPE,
        });
        ids.push(attached.id);
        attachedHere.current.add(attached.id);
        // Noted at once: a retry after a later failure must not attach it again.
        setPictures((current) =>
          current.map((x) => (x.key === p.key ? { ...x, id: attached.id, s3Key: undefined } : x)),
        );
      }
      const kept = new Set(ids);
      // Taken off: what the piece had when the form opened, and anything an
      // earlier, failed attempt of this save attached.
      const onPiece = [...(item?.pictures ?? []).map((p) => p.id), ...attachedHere.current];
      for (const id of onPiece) {
        if (!kept.has(id) && !deleted.current.has(id)) {
          await deleteEntityAttachment(id, {
            entityType: DECOR_LIBRARY_ENTITY_TYPE,
            entityId: saved.id,
          });
          deleted.current.add(id);
        }
      }
      const sameOrder =
        item &&
        ids.length === item.pictures.length &&
        ids.every((id, i) => item.pictures[i].id === id);
      if (!sameOrder) {
        saved = await update.mutateAsync({ id: saved.id, input: { pictureOrder: ids } });
      }
      setDirty(false);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(errorText(err, 'The piece could not be saved. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  const close = async () => {
    // Nothing closes the form while a save is being written.
    if (saving) return;
    if (
      dirty &&
      !(await confirm({
        title: 'Discard your changes?',
        message: 'This piece has changes that are not saved.',
        confirmLabel: 'Discard',
        variant: 'danger',
      }))
    ) {
      return;
    }
    onClose();
  };

  const deletePiece = async () => {
    if (!item) return;
    const ok = await confirm({
      title: `Delete ${item.name}?`,
      message:
        'It leaves the library with its pictures. Plans that already chose it keep how it looks.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(item.id);
      onClose();
    } catch (err) {
      setError(errorText(err, 'The piece could not be deleted. Try again.'));
    }
  };

  const spec = category ? DECOR_LIBRARY_CATEGORY_SPECS[category] : null;
  const busy = saving || uploading > 0;
  // While a save is being written or the AI is reading, the form holds still:
  // an edit made meanwhile would be overwritten by the answer or closed
  // unsaved by the save.
  const locked = saving || reading;
  lockedRef.current = locked;

  return (
    <EntityModal
      open={open}
      onClose={() => void close()}
      title={item ? `Edit ${item.name}` : 'Add to the decor library'}
      size="lg"
      // Above the library picker it may open from (z-[55]), below the in-app
      // dialogs (z-[60]) that ask before discarding or deleting.
      overlayClassName="z-[58]"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {item && (
            <button
              type="button"
              onClick={() => void deletePiece()}
              disabled={locked}
              className="mr-auto inline-flex items-center gap-1.5 min-h-11 sm:min-h-9 rounded-lg px-3 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={() => void close()}
            disabled={saving}
            className="min-h-11 sm:min-h-9 rounded-lg border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy || reading}
            className="min-h-11 sm:min-h-9 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : uploading ? 'Uploading…' : 'Save'}
          </button>
        </div>
      }
    >
      <fieldset disabled={locked} aria-busy={locked} className="m-0 min-w-0 space-y-5 border-0 p-0">
        {/* Pictures */}
        <section aria-label="Pictures" className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">
              Pictures{' '}
              <span className="font-normal text-gray-500">
                — every angle you have; the first is the cover
              </span>
            </h3>
            <button
              type="button"
              onClick={() => void fillWithAi()}
              disabled={pictures.length === 0 || read.isPending || reading || uploading > 0}
              title={pictures.length === 0 ? 'Add a picture first' : undefined}
              className="inline-flex items-center gap-1.5 min-h-11 sm:min-h-9 rounded-lg border border-primary-200 bg-primary-50 px-3 text-sm font-medium text-primary-700 hover:bg-primary-100 disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" />
              {reading ? 'Reading…' : 'Fill in with AI'}
            </button>
          </div>
          <div
            className="grid grid-cols-2 gap-2 sm:grid-cols-4"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (!locked && e.dataTransfer.files.length) void addFiles(e.dataTransfer.files);
            }}
          >
            {pictures.map((p, i) => (
              <figure
                key={p.key}
                className="group relative aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
              >
                {p.url ? (
                  <img
                    src={p.url}
                    alt={p.fileName ?? `Picture ${i + 1}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-gray-500">
                    {p.fileName}
                  </div>
                )}
                {i === 0 && (
                  <span className="absolute left-1.5 top-1.5 rounded-full bg-gray-900/75 px-2 py-0.5 text-[11px] font-medium text-white">
                    Cover
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      aria-label={`Move picture ${i + 1} earlier`}
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                      className="flex h-8 w-8 items-center justify-center rounded-md bg-white/90 text-gray-700 disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Move picture ${i + 1} later`}
                      disabled={i === pictures.length - 1}
                      onClick={() => move(i, 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-md bg-white/90 text-gray-700 disabled:opacity-40"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    {i > 0 && (
                      <button
                        type="button"
                        aria-label={`Make picture ${i + 1} the cover`}
                        onClick={() => move(i, -i)}
                        className="flex h-8 w-8 items-center justify-center rounded-md bg-white/90 text-amber-600"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove picture ${i + 1}`}
                    onClick={() => {
                      setPictures((current) => current.filter((x) => x.key !== p.key));
                      touch();
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-white/90 text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </figure>
            ))}
            {pictures.length < MAX_DECOR_LIBRARY_PICTURES && (
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-sm text-gray-500 hover:border-primary-400 hover:text-primary-600"
              >
                <ImagePlus className="h-6 w-6" />
                {uploading ? 'Uploading…' : 'Add pictures'}
                <span className="text-xs text-gray-400">or drop them here</span>
              </button>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            data-testid="decor-piece-files"
            onChange={(e) => {
              if (e.target.files?.length) void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </section>

        {note && (
          <p role="status" className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">
            {note}
          </p>
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {/* What it is */}
        <section className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="decor-piece-category" className="text-sm font-medium text-gray-700">
              Kind of piece
            </label>
            <SearchableListSelect
              inputId="decor-piece-category"
              items={[...DECOR_LIBRARY_CATEGORIES]}
              value={category}
              disabled={locked}
              onChange={(v) => {
                if (lockedRef.current) return;
                const next = v as DecorLibraryCategory;
                setCategory(next);
                setLook((current) => categoryLook(next, current));
                touch();
              }}
              getValue={(c) => c}
              getLabel={(c) => DECOR_LIBRARY_CATEGORY_SPECS[c].label}
              getSubtitle={(c) => DECOR_LIBRARY_CATEGORY_SPECS[c].hint}
              placeholder="Chairs, linens, bars…"
              clearable={false}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="decor-piece-name" className="text-sm font-medium text-gray-700">
              Name
            </label>
            <input
              id="decor-piece-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                touch();
              }}
              placeholder="Gold chiavari, ivory cushion"
              className={INPUT}
              maxLength={200}
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label htmlFor="decor-piece-description" className="text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="decor-piece-description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                touch();
              }}
              rows={2}
              maxLength={2000}
              className={`${INPUT} py-2`}
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label htmlFor="decor-piece-tags" className="text-sm font-medium text-gray-700">
              Tags
            </label>
            <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2 py-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-sm text-gray-700"
                >
                  {t}
                  <button
                    type="button"
                    aria-label={`Remove tag ${t}`}
                    onClick={() => {
                      setTags(tags.filter((x) => x !== t));
                      touch();
                    }}
                    className="text-gray-400 hover:text-gray-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
              <input
                id="decor-piece-tags"
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={onTagKey}
                onBlur={() => tagDraft.trim() && addTag()}
                placeholder={tags.length ? '' : 'gold, rustic, velvet… (Enter adds one)'}
                className="min-w-[8rem] flex-1 border-0 bg-transparent p-1 text-sm focus:outline-none"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => {
                setIsActive(e.target.checked);
                touch();
              }}
              className="h-4 w-4 rounded border-gray-300"
            />
            Offered on floor plans (untick to retire it without deleting it)
          </label>
        </section>

        {/* Who supplies it */}
        <section aria-label="Vendors" className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">
            Who can supply it{' '}
            <span className="font-normal text-gray-500">— as many vendors as carry it</span>
          </h3>
          {vendors.map((v, i) => (
            <div
              key={v.vendorId}
              className="flex flex-col gap-2 rounded-lg border border-gray-200 p-2 sm:flex-row sm:items-center"
            >
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                {v.name}
              </span>
              <input
                aria-label={`Notes for ${v.name}`}
                value={v.notes}
                onChange={(e) => {
                  setVendors(
                    vendors.map((x, k) => (k === i ? { ...x, notes: e.target.value } : x)),
                  );
                  touch();
                }}
                placeholder="Their code, price, minimum…"
                maxLength={500}
                className={`${INPUT} sm:max-w-xs`}
              />
              <button
                type="button"
                aria-label={`Remove ${v.name}`}
                onClick={() => {
                  setVendors(vendors.filter((x) => x.vendorId !== v.vendorId));
                  touch();
                }}
                className="flex min-h-11 min-w-11 items-center justify-center self-end rounded-lg text-gray-500 hover:bg-gray-100 sm:min-h-9 sm:min-w-9 sm:self-auto"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {vendors.length < MAX_DECOR_LIBRARY_VENDORS && (
            <SearchableSelect
              entityType="vendor"
              value=""
              onChange={() => {}}
              excludeIds={vendors.map((v) => v.vendorId)}
              placeholder="Add a vendor…"
              disabled={locked}
              onResultSelect={(r) => {
                if (lockedRef.current) return;
                setVendors((current) =>
                  current.some((v) => v.vendorId === r.id)
                    ? current
                    : [...current, { vendorId: r.id, name: r.label, notes: '' }],
                );
                touch();
              }}
            />
          )}
        </section>

        {/* How the 3D view draws it */}
        {spec && (
          <section
            aria-label="In the 3D view"
            className="space-y-2 rounded-xl border border-gray-200 p-4"
          >
            <h3 className="text-sm font-semibold text-gray-900">How the 3D view draws it</h3>
            <DecorFieldsEditor
              fields={spec.fields}
              look={look}
              onChange={(next) => {
                setLook(next);
                touch();
              }}
              inherited={DEFAULT_DECOR as unknown as Record<string, unknown>}
              inheritedLabel="Not set"
            />
          </section>
        )}
      </fieldset>
    </EntityModal>
  );
}
